import { useMemo, useRef, useState } from "react";
import Select, { GroupBase, MultiValue, SelectInstance, SingleValue } from "react-select";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import cn from "classnames";
import { OptionType, SelectProps, SelectValue } from "../model/customSelect.types";
import { useMobile } from "shared/hooks/useMobile.hook";
import { useOnClickOutside } from "shared/hooks/useOnClickOutside.hook";
import { AppDOM } from "shared/utils/app.utils";
import {
  CustomDropdownIndicator,
  CustomInput,
  CustomLazyLoadedMenuList,
  CustomMenuList,
  CustomMultiValue,
  CustomOption,
  CustomPlaceholder,
  CustomSingleValue,
} from "./CustomSelectComponents";
import styles from "./CustomSelect.module.scss";

export const CustomSelect = <CustomOptionType, IsMulti extends boolean = false>({
  options,
  className,
  isMulti,
  componentsClassNames,
  handleChange,
  menuPlacement = "auto",
  isDropDownMenuRelative = false,
  isInputTypingEnabled = false,
  selectedIndicator = "checkmark",
  placeholderDataTestId,
  lazyLoadedMenuList = false,
  lazyLoadedMenuListRowHeight,
  isDisabled = false,
  ...rest
}: SelectProps<CustomOptionType, IsMulti>) => {
  const isMobile = useMobile();
  const selectRef = useRef<SelectInstance<OptionType, boolean, GroupBase<OptionType>>>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState<number>(0);

  const emotionCache = useMemo(
    () =>
      createCache({
        key: "react-select-styles",
        container: AppDOM.appContainer,
        prepend: true,
        speedy: false,
      }),
    []
  );

  const blurSelect = () => {
    window.setTimeout(() => {
      selectRef.current?.blur();
    }, 0);
  };

  useOnClickOutside({
    ref: selectContainerRef,
    shadowRoot: AppDOM.shadowRootNode,
    handler: () => {
      if (isMobile) {
        setIsOpen(false);
        blurSelect();
      }
    },
  });

  const handleTouchStart = () => {
    setTouchStartTime(Date.now());
  };

  const handleTouchEnd = () => {
    const touchDuration = Date.now() - touchStartTime;
    if (touchDuration < 500) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleUpdateState = (value: MultiValue<OptionType> | SingleValue<OptionType>) => {
    handleChange(value as SelectValue<CustomOptionType, IsMulti>);

    if (!isMulti) return;

    setTimeout(() => {
      if (selectRef.current) {
        selectRef.current.focus();
      }
    }, 0);
  };

  return (
    <div
      ref={selectContainerRef}
      onClick={() => {
        if (isDisabled) return;
        setIsOpen((prev) => !prev);
      }}
      onTouchStart={() => {
        if (isMobile && !isDisabled) {
          handleTouchStart();
        }
      }}
      onTouchEnd={() => {
        if (isMobile && !isDisabled) {
          handleTouchEnd();
        }
      }}
      className={styles.selectContainer}
    >
      <CacheProvider value={emotionCache}>
        <Select
          ref={selectRef}
          menuIsOpen={isMobile && !isDisabled ? isOpen : undefined}
          isDisabled={isDisabled}
          className={cn(styles.select, className, { [styles.notSearchable]: !isInputTypingEnabled })}
          components={{
            MenuList: (props) =>
              lazyLoadedMenuList ? (
                <CustomLazyLoadedMenuList
                  {...props}
                  className={cn(props.className, componentsClassNames?.menuList)}
                  lazyLoadedMenuListRowHeight={lazyLoadedMenuListRowHeight}
                />
              ) : (
                <CustomMenuList {...props} className={cn(props.className, componentsClassNames?.menuList)} />
              ),
            DropdownIndicator: (props) => (
              <CustomDropdownIndicator
                {...props}
                className={cn(props.className, componentsClassNames?.dropdownIndicator)}
              />
            ),
            Option: (props) => (
              <CustomOption
                {...props}
                className={cn(props.className, componentsClassNames?.option)}
                selectedIndicator={selectedIndicator}
              />
            ),
            SingleValue: (props) => (
              <CustomSingleValue {...props} className={cn(props.className, componentsClassNames?.singleValue)} />
            ),
            MultiValue: (props) => (
              <CustomMultiValue {...props} className={cn(props.className, componentsClassNames?.multiValue)} />
            ),
            Placeholder: (props) => (
              <CustomPlaceholder
                {...props}
                className={cn(props.className, componentsClassNames?.placeholder)}
                dataTestId={placeholderDataTestId}
              />
            ),
            Input: (props) => <CustomInput {...props} className={cn(props.className, componentsClassNames?.input)} />,
          }}
          blurInputOnSelect={!isMulti}
          onMenuClose={blurSelect}
          classNamePrefix="react-select"
          options={options}
          isSearchable={isInputTypingEnabled}
          isMulti={isMulti}
          hideSelectedOptions={false}
          closeMenuOnSelect={!isMulti}
          onChange={handleUpdateState}
          menuPlacement={menuPlacement}
          classNames={{
            menu: () => cn(styles.menu, { [styles.menuRelative]: isDropDownMenuRelative }, componentsClassNames?.menu),
          }}
          {...rest}
        />
      </CacheProvider>
    </div>
  );
};
